import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getOpsDashboard, OpsDashboard, Severity } from '../api/dashboards';
import {
  Empty,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Section,
  Table,
} from '../../../design-system/ops';
import { PLATFORM_CURRENCY, formatMoney } from '../../../commodity/format/money';

/**
 * PI-3 Epic 4 S4.4 — the operations risk surface. The Monday-morning glance.
 *
 * Rewritten onto the design system. It previously carried its own colour table
 * (`#1971c2`, `#e8590c`, `#c92a2a`), its own pill, its own row border and its
 * own section heading — none of which the console's theme could reach, and
 * none of which matched the operations prototype. Every one of those is now a
 * component, so the screen describes what it shows and nothing about how.
 */

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

/**
 * Severity, in the console's own vocabulary.
 *
 * The three status colours are fixed across the whole console — the
 * prototype's theme picker says so in as many words — so mapping onto them
 * here means this screen agrees with every other screen about what "critical"
 * looks like, for free.
 */
const SEVERITY_TONE: Record<Severity, PillTone> = {
  info: 'neu',
  warning: 'warn',
  critical: 'bad',
};

const AdminOpsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OpsDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getOpsDashboard();
        if (!cancelled) setData(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar el dashboard de ops');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <OpsPage title="Riesgo operativo">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!data) {
    return (
      <OpsPage title="Riesgo operativo">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const openCase = (enrollmentId: string) => () => navigate(`/admin/support/case/${enrollmentId}`);

  const total =
    data.stuckEscrow.length +
    data.unresolvedDisputes.length +
    data.notificationFailures.length +
    data.bookingExceptions.length;

  return (
    <OpsPage
      title="Riesgo operativo"
      subtitle={`Generado ${fmt(data.generatedAt)} · ${total} incidencias`}
    >
      <KpiRow>
        <Kpi
          label="Escrow atorado"
          value={data.stuckEscrow.length}
          note={data.stuckEscrow.length ? 'Dinero sin liberar' : 'Nada retenido'}
          tone={data.stuckEscrow.length ? 'bad' : 'neutral'}
          alert={data.stuckEscrow.length > 0}
        />
        <Kpi
          label="Disputas abiertas"
          value={data.unresolvedDisputes.length}
          note="Sin resolver"
          tone={data.unresolvedDisputes.length ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Notificaciones fallidas"
          value={data.notificationFailures.length}
          note="Reintentos agotados"
          tone={data.notificationFailures.length ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Posibles ausencias"
          value={data.bookingExceptions.length}
          note="Sesión no iniciada"
          tone={data.bookingExceptions.length ? 'lav' : 'neutral'}
        />
      </KpiRow>

      <Section
        title="Escrow atorado"
        count={data.stuckEscrow.length}
        subtitle="Hito completado hace más de una hora y el pago sigue sin liberarse."
      >
        {data.stuckEscrow.length === 0 ? (
          <Empty title="Nada retenido" detail="Ningún hito espera liberación." />
        ) : (
          <Table
            columns="96px 1fr 120px 110px"
            head={['Severidad', 'Inscripción', 'Importe', 'Retenido']}
          >
            {data.stuckEscrow.map((r) => (
              <Row
                key={`${r.enrollmentId}-${r.milestoneId}`}
                onOpen={openCase(r.enrollmentId)}
                label={`Abrir la inscripción ${r.enrollmentId}`}
              >
                <Pill tone={SEVERITY_TONE[r.severity]}>{r.severity}</Pill>
                <span>
                  <b>#{r.sequence}</b>
                  <br />
                  <span className="mut">{r.enrollmentId.slice(0, 12)}</span>
                </span>
                <span>{formatMoney(r.amount, PLATFORM_CURRENCY)}</span>
                <span className="mut">{r.hoursStuck.toFixed(1)}h</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Disputas sin resolver"
        count={data.unresolvedDisputes.length}
        subtitle="Mientras siguen abiertas, el dinero del hito permanece congelado."
      >
        {data.unresolvedDisputes.length === 0 ? (
          <Empty title="Sin disputas abiertas" />
        ) : (
          <Table
            columns="96px 1fr 130px 110px"
            head={['Severidad', 'Disputa', 'Estado', 'Abierta']}
          >
            {data.unresolvedDisputes.map((d) => (
              <Row
                key={d.disputeId}
                onOpen={openCase(d.enrollmentId)}
                label={`Abrir la disputa ${d.disputeId}`}
              >
                <Pill tone={SEVERITY_TONE[d.severity]}>{d.severity}</Pill>
                <span>
                  <b>{d.milestoneId}</b>
                  <br />
                  <span className="mut">{d.disputeId.slice(0, 12)}</span>
                </span>
                <span>{d.status}</span>
                <span className="mut">{d.hoursOpen.toFixed(1)}h</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Notificaciones fallidas"
        count={data.notificationFailures.length}
        subtitle="Reintentos agotados: alguien esperaba un aviso que no llegó."
      >
        {data.notificationFailures.length === 0 ? (
          <Empty title="Todo entregado" />
        ) : (
          <Table columns="1fr 90px 110px" head={['Aviso', 'Intentos', 'Último']}>
            {data.notificationFailures.map((n) => (
              <Row
                key={n.notificationId}
                onOpen={openCase(n.enrollmentId)}
                label={`Abrir el caso de ${n.kind}`}
              >
                <span>
                  <b>{n.kind}</b>
                  {n.lastError ? (
                    <>
                      <br />
                      <span className="mut">{n.lastError}</span>
                    </>
                  ) : null}
                </span>
                <span>{n.attempts}</span>
                <span className="mut">{fmt(n.lastAttemptAt)}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Posibles ausencias"
        count={data.bookingExceptions.length}
        subtitle="La sesión debía haber empezado y nadie entró a la videollamada."
      >
        {data.bookingExceptions.length === 0 ? (
          <Empty title="Todas las sesiones arrancaron" />
        ) : (
          <Table
            columns="96px 1fr 120px 110px"
            head={['Severidad', 'Reserva', 'Estado', 'Retraso']}
          >
            {data.bookingExceptions.map((b) => (
              <Row
                key={b.bookingId}
                onOpen={openCase(b.enrollmentId)}
                label={`Abrir la reserva ${b.bookingId}`}
              >
                <Pill tone={SEVERITY_TONE[b.severity]}>{b.severity}</Pill>
                <span>
                  <b>#{b.milestoneSequence}</b>
                  <br />
                  <span className="mut">programada {fmt(b.scheduledStart)}</span>
                </span>
                <span>{b.state}</span>
                <span className="mut">{b.hoursPastStart.toFixed(1)}h</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>
    </OpsPage>
  );
};

export default AdminOpsDashboard;
